import { badgeOf } from 'features/battle/rules';
import type { BattleFx, BattleFxView, BattleState, PileIndex, PlayerId } from 'features/battle/types';

/** `activated`: a discard of a card that was just played, shown with the Activated label and ring. */
/** `badgeId` is resolved when the effect arrives, so the animation shows what the card was at that moment. */
export type BattleFxItem = BattleFx & {
  key: string;
  badgeId: string;
  activated?: boolean;
  /** Same-pile Revealed then Activated, shown as one longer pile label. */
  revealedAndActivated?: boolean;
};

/** Fx shown as a label on the pile itself. A newer one on the same pile replaces the older. */
export const PILE_LABEL_FX = new Set<BattleFx['kind']>(['play', 'reveal', 'upgrade', 'swap', 'remove_pile']);

export type TargetMark = { key: string; player: PlayerId; pile: PileIndex; badgeId: string };

/** One frame of the move being played out: the board to show, what animates, and how long before the next. */
export type PresentStep = { state: BattleState; fx: BattleFxItem[]; targets: TargetMark[]; holdMs: number };

/** One Prestige pop: neighbor bonuses and the card's own gain land as a single +X. */
export const prestigeFxDelta = (
  fx: readonly { kind: string; player: PlayerId; amount?: number }[],
  player: PlayerId
): number =>
  fx
    .filter((item) => item.kind === 'prestige' && item.player === player)
    .reduce((sum, item) => sum + (item.amount ?? 0), 0);

/** Extra hold on a combined Revealed & Activated pile label. */
export const REVEAL_AND_ACTIVATE_EXTRA_MS = 1000;

export const fxDoneMs = (item: BattleFx & { revealedAndActivated?: boolean }): number => {
  if (item.kind === 'draw' || item.kind === 'discard' || item.kind === 'exchange') return 3300;
  if (item.kind === 'return' && item.to !== 'deck') return 3300;
  if (item.kind === 'remove_pile') return 700;
  return item.revealedAndActivated ? 2500 + REVEAL_AND_ACTIVATE_EXTRA_MS : 2500;
};

/** Gap between animations from one opponent move, so an activation reads before what it causes. */
export const OPPONENT_FX_STEP_MS = 700;

/** How long a picked badge shows as "Targeted" before it leaves the board. */
export const TARGETED_HOLD_MS = 2000;

const isBoardMove = (item: BattleFx): boolean =>
  (item.kind === 'return' || item.kind === 'discard') && item.pile != null;

const samePileRevealPlay = (item: BattleFx, other: BattleFx): boolean =>
  other.kind === 'reveal' &&
  item.kind === 'play' &&
  other.player === item.player &&
  other.pile === item.pile &&
  other.cardId === item.cardId;

/** Starts a new step: an activation, a reveal, or a change of kind. Pop-ups ride along with what caused them. */
const startsStep = (item: BattleFx, step: BattleFxItem[], activated: Set<string>): boolean => {
  if (item.kind === 'stat' || item.kind === 'prestige') return false;
  // The badge a reveal pushed off its pile goes straight to the discard with that reveal.
  if (item.replaced) return false;
  // Both halves of a swap land together.
  if (item.kind === 'swap' && step.every((fx) => fx.kind === 'swap')) return false;
  // Revealed then Activated on the same badge is one pile label, not two queued actions.
  if (step.some((fx) => samePileRevealPlay(item, fx))) return false;
  // A badge its own effect takes off the board (e.g. "discard this badge") leaves one step after "Activated".
  if (
    isBoardMove(item) &&
    activated.has(item.cardId) &&
    step.some((fx) => fx.kind === 'play' && fx.cardId === item.cardId)
  ) {
    return true;
  }
  if (PILE_LABEL_FX.has(item.kind)) return true;
  return !step.every((fx) => fx.kind === item.kind && fx.to === item.to && isBoardMove(fx) === isBoardMove(item));
};

const collapseRevealActivate = (fx: BattleFxItem[]): BattleFxItem[] => {
  const out: BattleFxItem[] = [];
  for (const item of fx) {
    const revealAt = item.kind === 'play' ? out.findIndex((prev) => samePileRevealPlay(item, prev)) : -1;
    if (revealAt >= 0) {
      out[revealAt] = { ...item, revealedAndActivated: true };
      continue;
    }
    out.push(item);
  }
  return out;
};

/** Badge ids resolve against `live`; the viewer's hand waits for its own draw step. */
const withLiveCards = (state: BattleState, live: BattleState): BattleState =>
  state.cards === live.cards ? state : { ...state, cards: live.cards };

const viewerDrawIds = (fx: BattleFx[], viewing: PlayerId): string[] =>
  fx.filter((item) => item.kind === 'draw' && item.player === viewing).map((item) => item.cardId);

/** Drops a viewer draw that a snapshot put in hand before that draw's step. */
const withoutUnpresentedDraws = (
  state: BattleState,
  live: BattleState,
  viewing: PlayerId,
  stepFx: BattleFx[]
): BattleState => {
  const shown = new Set(viewerDrawIds([...state.fx, ...stepFx], viewing));
  const pending = viewerDrawIds(live.fx, viewing).filter((cardId) => !shown.has(cardId));
  if (!pending.length) return withLiveCards(state, live);
  const pendingSet = new Set(pending);
  const seat = state.players[viewing];
  if (!seat) return withLiveCards(state, live);
  const leaked = seat.hand.filter((cardId) => pendingSet.has(cardId));
  if (!leaked.length) return withLiveCards(state, live);
  const players = [...state.players] as BattleState['players'];
  players[viewing] = {
    ...seat,
    hand: seat.hand.filter((cardId) => !pendingSet.has(cardId)),
    deck: [...leaked, ...seat.deck],
  };
  return withLiveCards({ ...state, players }, live);
};

/**
 * Turns one new battle state into steps to show.
 * `instant`: everything at once, for the viewer's own moves.
 * Otherwise each step shows the board as it was right after its fx, and a badge an effect is about to take
 * off the board is marked as targeted on the step before it goes.
 * A viewer draw stays out of hand until that draw's step, so it does not pop in during "Activated".
 */
export const presentSteps = (
  live: BattleState,
  previous: BattleState | null,
  viewing: PlayerId,
  seq: { current: number },
  instant: boolean
): PresentStep[] => {
  const played = new Set(live.fx.filter((item) => item.kind === 'play').map((item) => `${item.player}:${item.cardId}`));
  const visible = live.fx;
  const toItem = (item: BattleFx, index: number): BattleFxItem => {
    const { view, ...rest } = item;
    return {
      ...rest,
      badgeId: view?.cards?.[item.cardId] ?? badgeOf(live, item.cardId),
      key: `${seq.current++}-${item.kind}-${item.player}-${item.cardId}-${index}`,
      activated: item.kind === 'discard' && played.has(`${item.player}:${item.cardId}`),
    };
  };
  if (instant || !previous || !visible.length) {
    return [{ state: live, fx: collapseRevealActivate(visible.map(toItem)), targets: [], holdMs: 0 }];
  }
  // Stepped, "Activated" already showed on the pile, so the later discard reads as a plain discard.
  const stepItem = (item: BattleFx, index: number): BattleFxItem => ({ ...toItem(item, index), activated: false });

  const activated = new Set(live.fx.filter((item) => item.kind === 'play').map((item) => item.cardId));
  const drafts: { fx: BattleFxItem[]; view?: BattleFxView; targets: TargetMark[] }[] = [];
  visible.forEach((item, index) => {
    const last = drafts[drafts.length - 1];
    const fx = stepItem(item, index);
    if (last && !startsStep(item, last.fx, activated)) {
      last.fx.push(fx);
      last.view = item.view ?? last.view;
    } else {
      drafts.push({ fx: [fx], view: item.view, targets: [] });
    }
  });
  for (const draft of drafts) {
    draft.fx = collapseRevealActivate(draft.fx);
  }
  // Mark each badge a player picked to take off the board on the step before, where it still sits on its pile.
  // Random or rule-based removals just go, with no mark.
  for (let i = drafts.length - 1; i >= 0; i--) {
    const moving = drafts[i].fx.filter((item) => isBoardMove(item) && item.picked && !activated.has(item.cardId));
    if (!moving.length) continue;
    if (i === 0) {
      drafts.unshift({ fx: [], targets: [] });
      i += 1;
    }
    drafts[i - 1].targets.push(
      ...moving.map((item) => ({
        key: item.key,
        player: item.player,
        pile: item.pile as PileIndex,
        badgeId: item.badgeId,
      }))
    );
  }

  let base = previous;
  return drafts.map((draft, index) => {
    const last = index === drafts.length - 1;
    // Keep the finished log on every frame. The board catches up step by step, and the face should
    // follow the play being made, not the hole it leaves on the way there.
    const state = last
      ? live
      : draft.view
        ? {
            ...base,
            players: draft.view.players,
            cards: draft.view.cards,
            statGain: draft.view.statGain,
            removedLeftPiles: draft.view.removedLeftPiles,
            pileCount: draft.view.pileCount,
            fx: draft.fx,
            log: live.log,
          }
        : { ...base, fx: draft.fx, log: live.log };
    base = state;
    return {
      state: withoutUnpresentedDraws(state, live, viewing, draft.fx),
      fx: draft.fx,
      targets: draft.targets,
      holdMs: draft.targets.length ? TARGETED_HOLD_MS : OPPONENT_FX_STEP_MS,
    };
  });
};
